/**
 * Inspections in progress (drafts) and sent inspections — shared by every staff account, on every device.
 *
 *   phone copy  IndexedDB, so closing the app, losing signal or a dead battery never loses work
 *   shared copy Supabase table `inspection_records` (only signed-in, active staff can read or write it)
 *
 * Every save lands on the phone first, then goes to the shared copy (straight away with signal,
 * otherwise as soon as the phone is back online). Opening the app pulls everyone's latest, so a draft
 * started on one phone can be resumed on any other. If two people edit the same draft, the last save wins;
 * each draft shows who edited it last.
 *
 * Photos in the shared copy are online links only — a photo still on the phone is uploaded first.
 * A draft is everything the Inspection page holds: details, services, attic answers + photos,
 * the quote being built and which step the agent was on.
 */
import { useSyncExternalStore } from 'react';
import { AUTH_PROVIDER, getSupabase } from './auth';
import { CURRENT_USER } from './currentUser';
import { isUploaded, uploadPending } from './photoStorage';

const DB_NAME = 'cie-app';
const STORE = 'drafts';
const TABLE = 'inspection_records';
export const SHARED = AUTH_PROVIDER === 'supabase';

let drafts = [];
let sent = []; // sent inspections: { id, token, report, leadId, source, sentAt, by }
let loaded = false;
let sync = { status: SHARED ? 'idle' : 'local', error: '', syncedAt: null };
const listeners = new Set();
const emit = () => {
  drafts = [...drafts];
  listeners.forEach((fn) => fn());
};

/* ---------- phone copy (IndexedDB) ---------- */
const openDb = () =>
  new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('No IndexedDB'));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE, { keyPath: 'id' });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const tx = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const result = fn(t.objectStore(STORE));
    t.oncomplete = () => resolve(result && result.result);
    t.onerror = () => reject(t.error);
  });
};

const putLocal = async (record) => {
  try {
    await tx('readwrite', (s) => s.put(record));
  } catch {
    /* kept in memory for this session */
  }
};
const removeLocal = async (id) => {
  try {
    await tx('readwrite', (s) => s.delete(id));
  } catch {
    /* nothing to remove */
  }
};

const load = async () => {
  if (loaded) return;
  loaded = true;
  try {
    drafts = (await tx('readonly', (s) => s.getAll())) || [];
  } catch {
    drafts = []; // private mode / no IndexedDB — drafts still work for this session
  }
  emit();
  refreshDrafts();
};

/* ---------- shared copy (Supabase) ---------- */
// every photo slot in a draft: the front of home + each attic slot
const draftPhotos = (d) => [d.frontPhoto, ...Object.values((d.atticData && d.atticData.photos) || {}).flat()].filter(Boolean);

const swapPhotos = (d, map) => {
  const swap = (src) => (map.has(src) ? map.get(src) : src);
  const photos = (d.atticData && d.atticData.photos) || null;
  return {
    ...d,
    frontPhoto: d.frontPhoto ? swap(d.frontPhoto) : d.frontPhoto,
    atticData: photos
      ? { ...d.atticData, photos: Object.fromEntries(Object.entries(photos).map(([k, list]) => [k, (list || []).map(swap)])) }
      : d.atticData,
  };
};

// photos still on the phone go online first; any that can't stay out of the shared copy until they do
const shareable = async (d) => {
  const local = draftPhotos(d).filter((src) => !isUploaded(src));
  if (!local.length) return { draft: d, uploaded: false };
  const { list } = await uploadPending(local);
  const map = new Map(local.map((src, i) => [src, list[i]]));
  const withLinks = swapPhotos(d, map);
  const stillLocal = new Map(list.filter((src) => !isUploaded(src)).map((src) => [src, null]));
  const forCloud = stillLocal.size ? stripLocal(withLinks) : withLinks;
  return { draft: forCloud, local: withLinks, uploaded: list.some(isUploaded) };
};
const stripLocal = (d) => {
  const keep = (src) => isUploaded(src);
  const photos = (d.atticData && d.atticData.photos) || null;
  return {
    ...d,
    frontPhoto: d.frontPhoto && keep(d.frontPhoto) ? d.frontPhoto : '',
    atticData: photos ? { ...d.atticData, photos: Object.fromEntries(Object.entries(photos).map(([k, list]) => [k, (list || []).filter(keep)])) } : d.atticData,
  };
};

const pending = new Map(); // id → record waiting to reach the shared copy
const timers = new Map();

const pushNow = async (id) => {
  const record = pending.get(id);
  const supabase = getSupabase();
  if (!record || !supabase) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // retried when back online
  const kind = record._kind || 'draft';
  try {
    const { draft, local, uploaded } = await shareable(record);
    // keep the phone copy's new photo links too (same draft, nothing else changed)
    if (uploaded && local && kind === 'draft') {
      drafts = drafts.map((d) => (d.id === id && d.updatedAt === record.updatedAt ? { ...local } : d));
      await putLocal(local);
      emit();
    }
    const { _kind, ...clean } = draft;
    const { error } = await supabase.from(TABLE).upsert({
      id,
      kind, // a sent inspection's edits keep it 'sent' (its sent report and link are left as they were)
      lead_id: draft.leadId || null,
      customer: draft.customer || null,
      address: draft.address || null,
      current_step: draft.currentStep || 0,
      data: clean,
      deleted: false,
      updated_at: record.updatedAt,
      updated_by_name: record.editedBy || CURRENT_USER.name,
    });
    if (error) throw error;
    if (pending.get(id) === record) pending.delete(id);
  } catch (err) {
    console.warn('Draft kept on the phone — will sync when possible:', err.message || err);
  }
};

const schedulePush = (record) => {
  if (!SHARED) return;
  pending.set(record.id, record);
  clearTimeout(timers.get(record.id));
  timers.set(record.id, setTimeout(() => pushNow(record.id), 1500));
};

const setSync = (patch) => {
  sync = { ...sync, ...patch };
  listeners.forEach((fn) => fn());
};

/**
 * Pull everyone's drafts and sent inspections. Newer copies replace older ones on this phone; drafts
 * someone discarded or sent elsewhere leave this phone; drafts only this phone had are shared.
 */
let refreshing = null;
export const refreshDrafts = () => {
  const supabase = getSupabase();
  if (!SHARED || !supabase) return Promise.resolve();
  if (refreshing) return refreshing;
  setSync({ status: 'syncing', error: '' });
  refreshing = (async () => {
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('id, kind, lead_id, data, report, token, deleted, updated_at, updated_by_name')
        .order('updated_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      const rows = new Map(data.map((r) => [r.id, r]));

      // drafts: newest copy wins
      const next = [];
      for (const local of drafts) {
        const row = rows.get(local.id);
        if (!row) {
          next.push(local);
          if (!pending.has(local.id)) schedulePush(local); // only on this phone so far → share it
        } else if (row.kind !== 'draft' || row.deleted) {
          await removeLocal(local.id); // sent or discarded on another phone
        } else if (new Date(row.updated_at) > new Date(local.updatedAt || 0) && !pending.has(local.id)) {
          const fresh = { ...row.data, id: row.id, updatedAt: row.updated_at, editedBy: row.updated_by_name };
          await putLocal(fresh);
          next.push(fresh);
        } else {
          next.push(local);
        }
      }
      const have = new Set(next.map((d) => d.id));
      for (const row of data) {
        if (row.kind === 'draft' && !row.deleted && !have.has(row.id)) {
          const fresh = { ...row.data, id: row.id, updatedAt: row.updated_at, editedBy: row.updated_by_name };
          await putLocal(fresh);
          next.push(fresh);
        }
      }
      drafts = next;

      // sent inspections, newest first
      sent = data
        .filter((r) => r.kind === 'sent' && !r.deleted && r.report)
        .map((r) => ({ id: r.id, token: r.token, report: r.report, leadId: r.lead_id, source: r.data && Object.keys(r.data).length ? r.data : null, sentAt: r.updated_at, by: r.updated_by_name }))
        // an edit still waiting to upload wins over what the server has
        .map((r) => (pending.has(r.id) && pending.get(r.id)._kind === 'sent' ? { ...r, source: { ...pending.get(r.id) } } : r));
      setSync({ status: 'ready', syncedAt: new Date().toISOString() });
      emit();
    } catch (err) {
      setSync({ status: 'error', error: err.message || 'Couldn’t load shared drafts' });
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    [...pending.keys()].forEach((id) => pushNow(id));
    refreshDrafts();
  });
}

/* ---------- what the app uses ---------- */
const byNewest = (a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || '');

export const newDraftId = () => `draft-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

export const saveDraft = async (draft) => {
  const record = { ...draft, updatedAt: new Date().toISOString(), editedBy: CURRENT_USER.name };
  drafts = [record, ...drafts.filter((d) => d.id !== record.id)];
  emit();
  await putLocal(record);
  schedulePush(record);
  return record;
};

// discard: gone from every phone
export const deleteDraft = async (id) => {
  drafts = drafts.filter((d) => d.id !== id);
  pending.delete(id);
  clearTimeout(timers.get(id));
  emit();
  await removeLocal(id);
  const supabase = getSupabase();
  if (SHARED && supabase) {
    const { error } = await supabase.from(TABLE).update({ deleted: true, updated_at: new Date().toISOString(), updated_by_name: CURRENT_USER.name }).eq('id', id);
    if (error) console.warn('Discard not shared yet:', error.message);
  }
};

/**
 * Sent: the draft becomes a sent inspection everyone can see (View / Edit from Previous reports).
 * The report keeps online photo links only.
 */
export const saveSent = async ({ id, token, report, leadId, source }) => {
  const entry = { id, token, report, leadId, source, sentAt: new Date().toISOString(), by: CURRENT_USER.name };
  sent = [entry, ...sent.filter((s) => s.id !== id)];
  drafts = drafts.filter((d) => d.id !== id);
  pending.delete(id);
  clearTimeout(timers.get(id));
  emit();
  await removeLocal(id);
  await pushSent(entry).catch((err) => console.warn('Sent inspection not shared yet:', err.message));
};

// the shared copy — also what makes the customer's /r/<token> link open (Supabase public_report)
const pushSent = async ({ id, token, report, leadId, source, sentAt, by }) => {
  const supabase = getSupabase();
  if (!SHARED || !supabase) return;
  const cleanSource = source ? stripLocal((await shareable(source)).draft) : null;
  const { error } = await supabase.from(TABLE).upsert({
    id,
    kind: 'sent',
    token,
    report,
    lead_id: leadId || null,
    customer: report && report.header ? report.header.customer : null,
    address: report && report.header ? report.header.address : null,
    data: cleanSource || {},
    deleted: false,
    updated_at: sentAt,
    updated_by_name: by || CURRENT_USER.name,
  });
  if (error) throw new Error(error.message);
};

// try again to share a sent inspection (sent with no signal) — throws if it still can't
export const shareSent = (id) => {
  const entry = sent.find((x) => x.id === id);
  if (!entry) return Promise.reject(new Error('This report isn’t on this phone any more'));
  return pushSent(entry);
};

/**
 * Edits to a sent inspection (Edit → Fix the quote / Edit report, then Save & exit). It stays Sent —
 * only its saved inspection changes, so the next Edit opens the latest version. Drafts are only
 * inspections that were never sent.
 */
export const saveSentEdit = (source) => {
  const now = new Date().toISOString();
  sent = sent.map((x) => (x.id === source.id ? { ...x, source: { ...source }, editedAt: now, editedBy: CURRENT_USER.name } : x));
  emit();
  schedulePush({ ...source, updatedAt: now, editedBy: CURRENT_USER.name, _kind: 'sent' });
};

// the latest saved copy of one draft (used when a queued Zoho submit finally goes out)
export const getDraft = async (id) => {
  await load();
  return drafts.find((d) => d.id === id) || null;
};

const subscribe = (fn) => {
  listeners.add(fn);
  load();
  return () => listeners.delete(fn);
};

export const useDrafts = () => {
  const list = useSyncExternalStore(subscribe, () => drafts);
  return [...list].sort(byNewest);
};

export const useSentInspections = () => useSyncExternalStore(subscribe, () => sent);
export const getSentEntry = (id) => sent.find((x) => x.id === id) || null;
export const useDraftSync = () => useSyncExternalStore(subscribe, () => sync);
